import type { Deal, PartialDeal } from "../types/deal";

// Characters that are invalid for folder names on Windows and most file systems
// Control characters are invalid in folder names, so matching them is intended.
// eslint-disable-next-line no-control-regex
const INVALID_FOLDER_CHARS = /[<>:"/\\|?*\x00-\x1F]/g;

export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Sanitize deal name to ensure it's safe for folder creation
 */
export const sanitizeDealName = (name: string): string => {
  return name.replace(INVALID_FOLDER_CHARS, "_").trim();
};

/**
 * Validate deal name doesn't contain invalid folder characters
 */
export const validateDealName = (name: string): ValidationError | null => {
  if (!name || name.trim() === "") {
    return { field: "deal_name", message: "Deal name is required" };
  }

  if (INVALID_FOLDER_CHARS.test(name)) {
    return {
      field: "deal_name",
      message: 'Deal name contains invalid characters (/ \\ : * ? " < > |)',
    };
  }

  return null;
};

/**
 * Convert string numbers to actual numbers (handles AI extraction issues and comma-separated numbers)
 */
export const normalizeNumberField = (value: any): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    // Remove commas and whitespace, then parse
    const cleaned = value.replace(/,/g, "").trim();
    if (cleaned === "") {
      return null;
    }
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }

  return null;
};

/**
 * Convert number to string if needed (for fields that expect strings)
 */
export const normalizeStringField = (value: any): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string") {
    return value.trim() === "" ? null : value.trim();
  }

  return String(value);
};

/**
 * Normalize all deal fields to ensure correct types before API submission
 */
export const normalizeDealData = (deal: PartialDeal): PartialDeal => {
  const normalized = { ...deal };

  // Remove tenant_column_ranges if it exists (from AI extraction data - not part of Deal model)
  if ("tenant_column_ranges" in normalized) {
    delete (normalized as any).tenant_column_ranges;
  }

  // Handle source field - if it's an object (from AI extraction), extract source_name or delete it
  // The source field is legacy; source_entity should be used instead
  if (
    normalized.source &&
    typeof normalized.source === "object" &&
    normalized.source !== null
  ) {
    const sourceObj = normalized.source as any;
    // If it has source_name, extract it; otherwise delete the object
    if (sourceObj.source_name && typeof sourceObj.source_name === "string") {
      (normalized as any).source = sourceObj.source_name;
    } else {
      // If source is an object but doesn't have source_name, delete it to avoid "[object Object]"
      delete (normalized as any).source;
    }
  }

  // Number fields that might come as strings from AI
  const numberFields: (keyof Deal)[] = [
    "loan_amount_day_one",
    "loan_amount_fully_funded",
    "ltv",
    "ltc",
    "dy",
    "icr",
    "dscr",
    "initial_loan_term",
    "full_loan_term",
    "margin",
    "rate_floor",
    "probability",
    "amortisation_amount",
    "amortisation_percentage",
    "arrangement_fee",
    "commitment_fee",
    "make_whole_fee_interestmonths",
    "facility_agent_fee",
    "servicing_fee",
    "exit_fee",
    "extension_fee",
    "equity_contribution",
    "box_folder_id",
    "covenant_ltv",
    "covenant_ltc",
    "covenant_icr",
    "covenant_dscr",
    "covenant_dy",
    "cashtrap_ltv",
    "cashtrap_ltc",
    "cashtrap_icr",
    "cashtrap_dscr",
    "cashtrap_dy",
    "irr_local",
    "irr_usd",
    "irr_local_net",
    "irr_usd_net",
    "wal",
    "acq_fac_loan_amount_day_one",
    "acq_fac_loan_amount_fully_funded",
    "acq_fac_ltpp",
    "acq_fac_dy",
    "acq_fac_icr",
    "acq_fac_dscr",
    "acq_fac_arrangement_fee",
    "capex_fac_loan_amount_day_one",
    "capex_fac_loan_amount_fully_funded",
    "capex_fac_ltc",
    "capex_fac_pt_capex_cost",
    "capex_fac_arrangement_fee",
    "int_res_fac_loan_amount_day_one",
    "int_res_fac_pt_cap_int_commitfees",
    "int_res_fac_arrangement_fee",

    // VAT Facility
    "vat_fac_loan_amount_day_one",
    "vat_fac_loan_amount_fully_funded",
    "vat_fac_percent_vat_covered",
    "vat_fac_arrangement_fee",

    // Development Facility
    "dev_fac_loan_amount_day_one",
    "dev_fac_loan_amount_fully_funded",
    "dev_fac_ltc",
    "dev_fac_ltv",
    "dev_fac_ltgdv",
    "dev_fac_arrangement_fee",
    "extension_condition_dscr",
    "extension_condition_icr",
    "extension_condition_ltv",
    "extension_condition_ltc",
    "extension_condition_dy",
    "extension_condition_occupancy",
    "tf_arrangement_fee",
    "tf_servicing_fee",
  ];

  numberFields.forEach((field) => {
    if (field in normalized) {
      (normalized as any)[field] = normalizeNumberField(
        (normalized as any)[field],
      );
    }
  });

  // String fields that might come as numbers
  const stringFields: (keyof Deal)[] = [
    "deal_name",
    "deal_name_alias",
    "source",
    "priority",
    "loan_amount_currency",
    "cm_rating",
    "facility_agent_name",
    "security_agent_name",
  ];

  stringFields.forEach((field) => {
    if (field in normalized) {
      (normalized as any)[field] = normalizeStringField(
        (normalized as any)[field],
      );
    }
  });

  // Sanitize deal name
  if (normalized.deal_name) {
    normalized.deal_name = sanitizeDealName(normalized.deal_name);
  }

  return normalized;
};

const ALWAYS_REQUIRED_FIELDS: Array<{ field: keyof Deal; label: string }> = [
  { field: "deal_name", label: "Deal Name" },
  { field: "status", label: "Status" },
  { field: "countries", label: "Countries" },
  { field: "sectors", label: "Sectors" },
];

/** Extra deal fields required once a deal reaches term sheet / closing statuses. */
export const ADVANCED_STATUS_REQUIRED_FIELDS: Array<{
  field: keyof Deal;
  label: string;
}> = [
  { field: "wal", label: "WAL" },
  { field: "cm_rating", label: "CM Rating" },
  { field: "irr_local", label: "IRR Local (%)" },
  { field: "irr_usd", label: "IRR USD (%)" },
  { field: "irr_local_net", label: "IRR Local (Post TenFifty Fees)" },
  { field: "irr_usd_net", label: "IRR USD (Post TenFifty Fees)" },
  { field: "target_closing", label: "Expected Closing Date" },
  { field: "proposed_ic_date", label: "IC Date" },
];

/** @deprecated Use ADVANCED_STATUS_REQUIRED_FIELDS */
export const TERM_SHEET_SUBMITTED_REQUIRED_FIELDS =
  ADVANCED_STATUS_REQUIRED_FIELDS;

const STATUSES_REQUIRING_ADVANCED_FIELDS = new Set([
  "term sheet submitted",
  "closed",
  "closed - apollo",
  "ic memo",
  "preview memo",
  "signed / committed",
  "tenfifty large loan",
]);

export function getStatusName(status: unknown): string {
  if (typeof status === "string") return status;
  if (status && typeof status === "object" && "status" in status) {
    const name = (status as { status?: unknown }).status;
    return typeof name === "string" ? name : "";
  }
  return "";
}

export const statusRequiresAdvancedDealFields = (
  status: unknown,
): boolean => {
  const name = getStatusName(status).trim().toLowerCase();
  return STATUSES_REQUIRING_ADVANCED_FIELDS.has(name);
};

/** Statuses where a deal (or add-on) is in closing and needs advisor fee quotes. */
const IN_CLOSING_STATUSES = new Set([
  "signed / committed",
  "preview memo",
  "ic memo",
]);

export const statusIsInClosing = (status: unknown): boolean =>
  IN_CLOSING_STATUSES.has(getStatusName(status).trim().toLowerCase());

/**
 * Advisor services every deal in closing needs a quote for. Must match the
 * API (`REQUIRED_ADVISOR_SERVICES` in advisor_fee_completeness.py).
 */
export const REQUIRED_ADVISOR_SERVICES = ["Valuation", "Insurance", "Legal"];

export const isTermSheetSubmittedStatus = (status: unknown): boolean => {
  return getStatusName(status).trim().toLowerCase() === "term sheet submitted";
};

/** Empty string / null / [] count as missing. 0 is a valid number. */
export const isDealFieldFilled = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "number") return Number.isFinite(value);
  return true;
};

/**
 * Validate required fields for deal creation
 */
export const validateDealForCreation = (
  deal: PartialDeal,
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Required fields
  if (!deal.deal_name || deal.deal_name.trim() === "") {
    errors.push({ field: "deal_name", message: "Deal name is required" });
  } else {
    const nameError = validateDealName(deal.deal_name);
    if (nameError) {
      errors.push(nameError);
    }
  }

  if (!deal.status) {
    errors.push({ field: "status", message: "Status is required" });
  }

  if (!deal.countries || deal.countries.length === 0) {
    errors.push({
      field: "countries",
      message: "At least one country is required",
    });
  }

  if (!deal.sectors || deal.sectors.length === 0) {
    errors.push({
      field: "sectors",
      message: "At least one sector is required",
    });
  }

  if (statusRequiresAdvancedDealFields(deal.status)) {
    ADVANCED_STATUS_REQUIRED_FIELDS.forEach(({ field, label }) => {
      if (!isDealFieldFilled((deal as Record<string, unknown>)[field])) {
        errors.push({ field, message: `${label} is required` });
      }
    });
  }

  return errors;
};

/**
 * Get list of required fields with their labels.
 * Extra fields are required for Term Sheet Submitted and later closing
 * statuses. A kill reason is asked for but not required.
 */
export const getRequiredFields = (
  deal?: PartialDeal | null,
): Array<{
  field: keyof Deal;
  label: string;
}> => {
  if (statusRequiresAdvancedDealFields(deal?.status)) {
    return [...ALWAYS_REQUIRED_FIELDS, ...ADVANCED_STATUS_REQUIRED_FIELDS];
  }
  return [...ALWAYS_REQUIRED_FIELDS];
};

/**
 * Normalize date field - handles string dates in various formats (DD/MM/YYYY, YYYY-MM-DD, etc.)
 * Returns ISO date string (YYYY-MM-DD) or null
 */
export const normalizeDateField = (value: any): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  let dateObj: Date | null = null;

  if (value instanceof Date) {
    dateObj = value;
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") {
      return null;
    }

    // Handle DD/MM/YYYY format
    const ddmmyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
    if (ddmmyyyy) {
      const day = parseInt(ddmmyyyy[1], 10);
      const month = parseInt(ddmmyyyy[2], 10) - 1; // Month is 0-indexed
      const year = parseInt(ddmmyyyy[3], 10);
      dateObj = new Date(year, month, day);
      if (isNaN(dateObj.getTime())) {
        return null;
      }
    } else {
      // Try standard date parsing
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        dateObj = parsed;
      }
    }
  }

  if (dateObj && !isNaN(dateObj.getTime())) {
    // Convert to ISO date string (YYYY-MM-DD)
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return null;
};

/**
 * Normalize asset data for API submission - converts string numbers to actual numbers,
 * handles date formats, and cleans up field names
 */
export const normalizeAssetData = (asset: any): any => {
  const normalized: any = { ...asset };

  // Map count__boxes/count__rooms/count__units to count_boxes/count_rooms/count_units
  if ("count__boxes" in normalized) {
    normalized.count_boxes = normalized.count__boxes;
    delete normalized.count__boxes;
  }
  if ("count__rooms" in normalized) {
    normalized.count_rooms = normalized.count__rooms;
    delete normalized.count__rooms;
  }
  if ("count__units" in normalized) {
    normalized.count_units = normalized.count__units;
    delete normalized.count__units;
  }

  // Numeric fields that need to be converted from strings to numbers
  const numberFields = [
    "longitude",
    "latitude",
    "valuation_amount",
    "acquisition_price",
    "year_built",
    "year_refurbished",
    "count_buildings",
    "count_floors",
    "total_area",
    "vacancy",
    "walt",
    "walb",
    "passing_rent",
    "contracted_rent",
    "estimated_rental_value",
    "tf_erv",
    "count_rooms",
    "count_boxes",
    "count_units",
    "opex_annual",
    "noi",
    "capex_total",
  ];

  numberFields.forEach((field) => {
    if (field in normalized) {
      normalized[field] = normalizeNumberField(normalized[field]);
    }
  });

  // Date fields that need normalization
  const dateFields = ["valuation_date", "acquisition_date"];
  dateFields.forEach((field) => {
    if (field in normalized) {
      normalized[field] = normalizeDateField(normalized[field]);
    }
  });

  // Handle country - extract ID if it's an object
  if (normalized.country) {
    if (Array.isArray(normalized.country) && normalized.country.length > 0) {
      normalized.country_id =
        normalized.country[0]?.id || normalized.country[0];
    } else if (
      typeof normalized.country === "object" &&
      normalized.country.id
    ) {
      normalized.country_id = normalized.country.id;
    } else if (typeof normalized.country === "number") {
      normalized.country_id = normalized.country;
    }
  }

  // Handle sector - extract ID if it's an object
  if (normalized.sector) {
    if (Array.isArray(normalized.sector) && normalized.sector.length > 0) {
      normalized.primary_sector_id =
        normalized.sector[0]?.id || normalized.sector[0];
    } else if (typeof normalized.sector === "object" && normalized.sector.id) {
      normalized.primary_sector_id = normalized.sector.id;
    } else if (typeof normalized.sector === "number") {
      normalized.primary_sector_id = normalized.sector;
    }
  }

  // Handle deal - extract ID if it's an object, or set to null if explicitly null/empty
  if (
    normalized.deal === null ||
    normalized.deal === undefined ||
    (Array.isArray(normalized.deal) && normalized.deal.length === 0)
  ) {
    // Explicitly set deal_id to null when deal is cleared
    normalized.deal_id = null;
  } else if (normalized.deal) {
    if (Array.isArray(normalized.deal) && normalized.deal.length > 0) {
      normalized.deal_id = normalized.deal[0]?.id || normalized.deal[0];
    } else if (typeof normalized.deal === "object" && normalized.deal.id) {
      normalized.deal_id = normalized.deal.id;
    } else if (typeof normalized.deal === "number") {
      normalized.deal_id = normalized.deal;
    }
  }
  // If deal_id is explicitly set to null, preserve it (don't override)
  if ("deal_id" in normalized && normalized.deal_id === null) {
    normalized.deal_id = null;
  }

  // Remove temporary fields that shouldn't be sent to API
  const {
    id: _tempId,
    deal: _dealObj,
    country: _countryObj,
    sector: _sectorObj,
    ...rest
  } = normalized;

  // Keep id if it's a number (existing asset), but remove if it's a temp string
  if (typeof normalized.id === "number") {
    rest.id = normalized.id;
  }

  // Keep tenants separately if they exist (will be handled separately in some cases)
  if (normalized.tenants && Array.isArray(normalized.tenants)) {
    rest.tenants = normalized.tenants;
  }

  // Preserve cached places fields (JSON/JSONB fields)
  if (normalized.cached_train_stations !== undefined) {
    rest.cached_train_stations = normalized.cached_train_stations;
  }
  if (normalized.cached_airports !== undefined) {
    rest.cached_airports = normalized.cached_airports;
  }

  return rest;
};
